// Golf Match Play Tracker 2026 -- Master calendar
//
// Everything here is a STARTING POINT. Every field is editable in the
// Schedule tab: dates, courses, which nine, first tee time, interval,
// number of slots, and which divisions play where.

export const COURSES = [
  { id: "7185", name: "Champions Pointe", short_name: "Champions", sort_order: 1 },
  { id: "7186", name: "Covered Bridge",   short_name: "Covered Bridge", sort_order: 2 },
];

// Tee sets seeded with last year's colours. Ratings/slopes start EMPTY --
// enter them on the Courses tab (or sync from GHIN) so cross-tee stroke
// allocation is exact.
//
// tee_tier groups equivalent tees ACROSS courses, so a player picks a tee
// level once and it resolves correctly wherever they play:
//
//   Tier 1   Champions Pointe White  |  Covered Bridge Black
//   Tier 2   Champions Pointe Gray   |  Covered Bridge Gold
export const TEE_SETS = [
  { id: "7185_white", course_id: "7185", name: "White", sort_order: 1, tee_tier: 1 },
  { id: "7185_gray",  course_id: "7185", name: "Gray",  sort_order: 2, tee_tier: 2 },
  { id: "7186_black", course_id: "7186", name: "Black", sort_order: 1, tee_tier: 1 },
  { id: "7186_gold",  course_id: "7186", name: "Gold",  sort_order: 2, tee_tier: 2 },
];

// Tier every new player starts on. Tier 1 = White / Black, matching the
// 2025 app's White default. Change per player on the Roster tab.
export const DEFAULT_TEE_TIER = 1;

const CP = "7185"; // Champions Pointe
const CB = "7186"; // Covered Bridge

// ---------------------------------------------------------------------
// REGULAR SEASON -- 7 weeks, 8-player round robin per division.
//
// Division pairings rotate weekly so every division shares tee times with
// every other division. On the two split-course weeks (Oct 7 / Oct 14)
// Woods and Palmer are always at DIFFERENT courses, and Oct 14 is the
// mirror of Oct 7.
// ---------------------------------------------------------------------
export const SEASON_DATES = [
  {
    id: "sd_w1", seq: 1, play_date: "2026-09-01", label: "Week 1", phase: "regular",
    split_courses: false,
    pairings: [["Woods", "Palmer"], ["Nicklaus", "Hogan"]],
    assignments: [
      { course_id: CP, nine: "F9", divisions: ["Woods","Nicklaus","Palmer","Hogan"],
        first_tee_time: "16:50", interval_min: 10, slot_count: 8 },
    ],
  },
  {
    id: "sd_w2", seq: 2, play_date: "2026-09-08", label: "Week 2", phase: "regular",
    split_courses: false,
    pairings: [["Woods", "Hogan"], ["Nicklaus", "Palmer"]],
    assignments: [
      { course_id: CP, nine: "F9", divisions: ["Woods","Nicklaus","Palmer","Hogan"],
        first_tee_time: "16:50", interval_min: 10, slot_count: 8 },
    ],
  },
  {
    id: "sd_w3", seq: 3, play_date: "2026-09-15", label: "Week 3", phase: "regular",
    split_courses: false,
    pairings: [["Woods", "Nicklaus"], ["Palmer", "Hogan"]],
    assignments: [
      { course_id: CP, nine: "F9", divisions: ["Woods","Nicklaus","Palmer","Hogan"],
        first_tee_time: "16:30", interval_min: 10, slot_count: 8 },
    ],
  },
  {
    id: "sd_w4", seq: 4, play_date: "2026-09-23", label: "Week 4", phase: "regular",
    split_courses: false,
    pairings: [["Woods", "Palmer"], ["Nicklaus", "Hogan"]],
    assignments: [
      { course_id: CB, nine: "F9", divisions: ["Woods","Nicklaus","Palmer","Hogan"],
        first_tee_time: "16:30", interval_min: 10, slot_count: 8 },
    ],
  },
  {
    id: "sd_w5", seq: 5, play_date: "2026-09-30", label: "Week 5", phase: "regular",
    split_courses: false,
    pairings: [["Woods", "Hogan"], ["Nicklaus", "Palmer"]],
    assignments: [
      { course_id: CB, nine: "F9", divisions: ["Woods","Nicklaus","Palmer","Hogan"],
        first_tee_time: "16:20", interval_min: 10, slot_count: 8 },
    ],
  },
  {
    // SPLIT WEEK -- Woods & Nicklaus at Champions Pointe,
    //               Palmer & Hogan at Covered Bridge.
    id: "sd_w6", seq: 6, play_date: "2026-10-07", label: "Week 6 (split courses)",
    phase: "regular", split_courses: true,
    pairings: [["Woods", "Nicklaus"], ["Palmer", "Hogan"]],
    assignments: [
      { course_id: CP, nine: "F9", divisions: ["Woods","Nicklaus"],
        first_tee_time: "16:30", interval_min: 10, slot_count: 4 },
      { course_id: CB, nine: "F9", divisions: ["Palmer","Hogan"],
        first_tee_time: "16:30", interval_min: 10, slot_count: 4 },
    ],
  },
  {
    // SPLIT WEEK -- mirror of Week 6: courses swapped.
    id: "sd_w7", seq: 7, play_date: "2026-10-14", label: "Week 7 (split courses, reversed)",
    phase: "regular", split_courses: true,
    pairings: [["Woods", "Nicklaus"], ["Palmer", "Hogan"]],
    assignments: [
      { course_id: CB, nine: "F9", divisions: ["Woods","Nicklaus"],
        first_tee_time: "16:30", interval_min: 10, slot_count: 4 },
      { course_id: CP, nine: "F9", divisions: ["Palmer","Hogan"],
        first_tee_time: "16:30", interval_min: 10, slot_count: 4 },
    ],
  },

  // -------------------------------------------------------------------
  // PLAYOFFS -- top 4 from each division = 16 players, single elimination
  // -------------------------------------------------------------------
  {
    id: "sd_p1", seq: 8, play_date: "2026-10-17",
    label: "Playoffs Day 1 - Champions Pointe", phase: "playoff",
    split_courses: false, pairings: [],
    assignments: [
      // Round of 16: 4 foursomes = 8 matches
      { course_id: CP, nine: "F9", divisions: [], bracket_round: 1,
        first_tee_time: "09:30", interval_min: 10, slot_count: 4 },
      // Quarterfinals: 2 foursomes = 4 matches
      { course_id: CP, nine: "F9", divisions: [], bracket_round: 2,
        first_tee_time: "12:30", interval_min: 10, slot_count: 2 },
    ],
  },
  {
    id: "sd_p2", seq: 9, play_date: "2026-10-18",
    label: "Playoffs Day 2 - Covered Bridge", phase: "playoff",
    split_courses: false, pairings: [],
    assignments: [
      // Semifinals: 2 twosomes = 2 matches, front 9
      { course_id: CB, nine: "F9", divisions: [], bracket_round: 3,
        first_tee_time: "10:00", interval_min: 10, slot_count: 2 },
      // Championship: 1 twosome = 1 match, back 9
      { course_id: CB, nine: "B9", divisions: [], bracket_round: 4,
        first_tee_time: "12:30", interval_min: 10, slot_count: 1 },
    ],
  },
];

export const BRACKET_ROUND_LABELS = {
  1: "Round of 16",
  2: "Quarterfinals",
  3: "Semifinals",
  4: "Championship",
};
